import api from './api';

// Types - Updated to match new API response structure
export interface ShippingRate {
  id: string;
  provider: string;
  provider_display_name: string;
  service_id: string;
  name: string;
  description: string;
  price: number;
  base_price: number;
  has_commission: boolean;
  currency: string;
  original_currency: string;
  transit_days: number;
  estimated_delivery_date: string;
  carrier_name: string;
  service_type: string;
  transit_time: string;
  insurance_up_to: string;
  printing_required: boolean;
  signature_required: boolean;
  doorstep_delivery: boolean;
  online_tracking: boolean;
  price_business_delivery: {
    amount: number;
    currency: string;
  };
  price_private_delivery: {
    amount: number;
    currency: string;
  };
}

export interface ParcelData {
  quantity: number;
  weight: number;
  length: number;
  width: number;
  height: number;
  physicalWeight: number;
  dimensionalWeight: number;
}

export interface RateRequestData {
  origin: string;
  destination: string;
  originPostalCode: string;
  destinationPostalCode: string;
  /** City names when the address is known; carriers such as Aramex require city or postal code. */
  originCity?: string;
  destinationCity?: string;
  shippingType: 'package' | 'document';
  shipmentType: 'international' | 'domestic';
  parcels: ParcelData[];
  totalWeight: number;
  unitSystem: 'metric' | 'imperial';
  isInternational: boolean;
  compareAllProviders: boolean;
}

// Updated to match the actual API response format
export interface RateResponseData {
  success: boolean;
  provider?: string;
  provider_description?: string;
  providers?: string[];
  services: {
    id: string;
    provider: string;
    provider_display_name: string;
    service_id: string;
    name: string;
    description: string;
    price: number;
    base_price: number;
    has_commission: boolean;
    currency: string;
    original_currency: string;
    transit_days: number;
    estimated_delivery_date: string;
    carrier_name: string;
    service_type: string;
    transit_time: string;
    insurance_up_to: string;
    printing_required: boolean;
    signature_required: boolean;
    doorstep_delivery: boolean;
    online_tracking: boolean;
    price_business_delivery: {
      amount: number;
      currency: string;
    };
    price_private_delivery: {
      amount: number;
      currency: string;
    };
  }[];
  error_message: string | null;
}

// API base URL - should be configured in environment variables in a real app
const API_BASE_URL = '/shipping';

// Shipping service methods
const shippingService = {
  /**
   * Get shipping rates based on provided parameters
   */
  getRates: async (requestData: RateRequestData, currency?: string): Promise<RateResponseData> => {
    try {
      // `currency` overrides the display currency (X-Currency) for flows that must price in a specific wallet currency.
      const response = await api.post(`${API_BASE_URL}/rates`, requestData, currency ? { headers: { 'X-Currency': currency } } : undefined);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching shipping rates:', error);
      throw error;
    }
  },

  /**
   * Track a shipment by tracking number with optional provider
   */
  trackShipment: async (trackingNumber: string, provider?: string): Promise<any> => {
    try {
      const url = provider 
        ? `${API_BASE_URL}/track/${trackingNumber}?provider=${provider.toLowerCase()}`
        : `${API_BASE_URL}/track/${trackingNumber}`;
      const response = await api.get(url);
      return response.data.data;
    } catch (error: any) {
      console.error('Error tracking shipment:', error);
      const apiMessage = error?.response?.data?.message;
      if (apiMessage) {
        throw new Error(apiMessage);
      }
      throw error;
    }
  },

  /**
   * Create a shipping label
   */
  createLabel: async (shippingData: any): Promise<any> => {
    try {
      const response = await api.post(`${API_BASE_URL}/label`, shippingData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating shipping label:', error);
      throw error;
    }
  },

  /**
   * Validate an address
   */
  validateAddress: async (addressData: any): Promise<any> => {
    try {
      const response = await api.post(`${API_BASE_URL}/validate-address`, addressData);
      return response.data.data;
    } catch (error) {
      console.error('Error validating address:', error);
      throw error;
    }
  }
};

// Tracking types based on the new API response
export interface TrackingEvent {
  status: string;
  location: string;
  timestamp: string;
  description: string;
}

export interface ProofOfDelivery {
  has_proof_of_delivery: boolean;
  content: string; // Base64 encoded PDF
  format: 'PDF';
}

export interface TrackingData {
  success: boolean;
  provider: string;
  tracking_number: string;
  status: string;
  status_description: string;
  events: TrackingEvent[];
  estimated_delivery_date: string | null;
  actual_delivery_date: string | null;
  ship_date: string | null;
  proof_of_delivery: ProofOfDelivery | null;
  error_message: string | null;
}

export default shippingService; 
